import React, { useState } from 'react';
import { Check, Shield, Star, Clock, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const PricingSection = () => {
  const { login, isAuthenticated, hasAccess } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showWaitingMessage, setShowWaitingMessage] = useState(false);

  const features = [
    t('feature1'),
    t('feature2'),
    t('feature3'),
    t('feature4'),
    t('feature5'),
    t('feature6')
  ];

  const handleEnroll = () => {
    if (!isAuthenticated) {
      login();
    } else {
      // User is logged in but doesn't have access - show waiting message
      setShowWaitingMessage(true);
    }
  };

  const handleAccessCourse = () => {
    navigate('/course');
  };

  return (
    <section id="enroll" className="py-16 md:py-24 bg-[#1a1f2e]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('investInTransformation')}
          </h2>
          <p className="text-gray-400 text-lg">
            {t('pricingDesc')}
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="relative bg-[#232836] rounded-2xl border border-gray-700 overflow-hidden">
            <div className="absolute top-0 right-6">
              <span className="inline-block px-4 py-2 bg-[#d4a33a] text-white text-xs font-bold uppercase tracking-wide">
                {t('mostPopular')}
              </span>
            </div>

            <div className="p-8 pt-12">
              <h3 className="text-2xl font-bold text-white text-center mb-4">
                {t('fullCourseAccess')}
              </h3>

              <div className="text-center mb-2">
                <span className="text-gray-400 line-through text-xl mr-2">€2222</span>
                <span className="text-[#d4a33a] text-5xl font-bold">€1111</span>
              </div>

              <p className="text-gray-400 text-center text-sm mb-8">
                {t('oneTimePayment')}
              </p>

              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#d4a33a]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-[#d4a33a]" />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              {hasAccess ? (
                <>
                  <button
                    onClick={handleAccessCourse}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors duration-300"
                  >
                    <Check className="w-5 h-5" />
                    {t('accessCourse')}
                  </button>
                  
                  {/* Success Message - Purchase Complete */}
                  <div className="mt-6 relative p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 overflow-hidden">
                    <div className="absolute inset-0 bg-green-500/5 animate-pulse"></div>
                    <div className="absolute -inset-1 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-green-500/20 blur-xl animate-pulse"></div>
                    
                    <div className="relative flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center animate-pulse">
                        <Sparkles className="w-6 h-6 text-green-400" />
                      </div>
                      <div className="text-right flex-1" dir="rtl">
                        <p className="text-green-400 font-bold text-lg mb-1 animate-pulse">
                          لقد تمت عملية الشراء
                        </p>
                        <p className="text-green-300 text-sm">
                          محتوى الكورس متاح الآن
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={handleEnroll}
                    className="w-full bg-[#d4a33a] hover:bg-[#c49530] text-white py-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors duration-300"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {isAuthenticated ? t('completeEnrollment') : t('signInToEnroll')}
                  </button>

                  {/* Waiting Message - Shows AFTER clicking Complete Enrollment */}
                  {isAuthenticated && showWaitingMessage && (
                    <div className="mt-6 relative p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 overflow-hidden">
                      <div className="absolute inset-0 bg-amber-500/5 animate-pulse"></div>
                      <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 blur-xl animate-pulse"></div>
                      
                      <div className="relative flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                          <Clock className="w-6 h-6 text-amber-400 animate-spin" style={{ animationDuration: '3s' }} />
                        </div>
                        <div className="text-right flex-1" dir="rtl">
                          <p className="text-amber-400 font-bold text-lg mb-1 animate-pulse">
                            سيكون الكورس متاح
                          </p>
                          <p className="text-amber-300 text-sm">
                            بعد اتمام عملية الشراء
                          </p>
                          <p className="text-amber-300 text-sm">
                            وتفعيل اشتراكك من قِبل الأدمن
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-center gap-6 mt-6 text-gray-400 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>{t('securePayment')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  <span>{t('satisfactionGuarantee')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
